import { useContext, useEffect, useState } from 'react';

import { AlertContext } from 'contexts/alert-context';
import { AuthContext } from 'contexts/auth';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import useBanksList from 'hooks/use-banks-list';
import { usePost } from 'hooks/use-post';
import { CEF_CODE } from 'pages/bank-account/constants';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { BANK_ACCOUNT_URL } from 'utils/api-urls';
import InputLabelError from 'utils/old-ui-commons/input-label-error';

import { Box, Flex } from '@olist/united';

import { BankAccountFormControls } from './bank-account-form-controls';
import { AccountAndDigitInputs } from './final-form-fields/account-digit-inputs';
import { AccountTypeSelector } from './final-form-fields/account-type-selector';
import { SelectFilterField } from './final-form-fields/select-filter-field';
import { bankAccountFormFields, bankAccountPayloadSerializer } from './schema';
import { validateBankAccountForm } from './schema/bank-account-form.validators';

export interface IBankAccountForm {
  setSuccess: (value: boolean) => void;
  setOpenModal: (value: boolean) => void;
}

type ISelectBank = {
  label: string;
  value: string;
  accountTypes: [
    {
      label: string;
      value: string;
    }
  ];
  agencyPlaceholder: string;
  agencyRegex: string;
  accountPlaceholder: string;
  accountRegex: string;
};

type IAccountType = {
  value: string;
  label: string;
};

const AccountTypeInitialValue: IAccountType = {
  value: '',
  label: '',
};

const SelectedBankInitialValue: ISelectBank = {
  label: '',
  value: '',
  accountTypes: [{ label: '', value: '' }],
  agencyPlaceholder: '',
  agencyRegex: '',
  accountPlaceholder: '',
  accountRegex: '',
};

type IForm = {
  account_type: IAccountType;
  agency: string;
  agency_digit: string;
  bank: ISelectBank;
  bank_account: string;
  bank_account_digit: string;
};

type IHandleValidation = {
  [key: string]: Partial<IForm | ISelectBank | IAccountType>;
};

const getChangeCallback = (form: any) => {
  const { ACCOUNT_TYPE } = bankAccountFormFields;
  form.change(ACCOUNT_TYPE, null);
};

export const BankAccountForm = ({ setOpenModal, setSuccess }: IBankAccountForm) => {
  const { t } = useTranslation();

  const { defaultPlaceholders, optionsBank } = useBanksList();
  const { userInfo } = useContext(AuthContext);
  const { setAlert } = useContext(AlertContext);

  const { error, isSuccess, submit } = usePost(BANK_ACCOUNT_URL);

  const [accountType, setAccountType] = useState<IAccountType | undefined>(AccountTypeInitialValue);
  const [isCEF, setIsCEF] = useState(false);
  const [selectedBank, setSelectedBank] = useState(SelectedBankInitialValue);

  useEffect(() => {
    if (error) {
      const alertInfo = {
        text: t`there was an error submiting the information. try again.`,
        show: true,
        variation: 'danger',
        duration: 3000,
        iconName: 'alert-triangle-outline',
      };

      setAlert(alertInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, setAlert]);

  useEffect(() => {
    const isCEFSelected = selectedBank?.value === CEF_CODE;
    setIsCEF(isCEFSelected);
  }, [selectedBank]);

  const onSubmit = async (values: any) => {
    const { documentNumber: cnpj, id: sellerId } = userInfo;
    const serialized = bankAccountPayloadSerializer({
      cnpj,
      isCEF,
      sellerId,
      values,
    });

    submit(serialized);
  };

  useEffect(() => {
    if (isSuccess) {
      sendGTMEvent('etapa_cadastro', {
        etapa: 'dados_bancarios',
      });
      setSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const handleValidation = (values: IHandleValidation) => {
    const { ACCOUNT_TYPE, BANK } = bankAccountFormFields;
    if (selectedBank !== (values[BANK] as ISelectBank)) {
      setSelectedBank(values[BANK] as ISelectBank);
      setAccountType(undefined);
    } else {
      setAccountType(values[ACCOUNT_TYPE] as IAccountType);
    }
    return validateBankAccountForm({ fields: bankAccountFormFields, values });
  };

  const handleOpenModal = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setOpenModal(true);
  };
  return (
    <Form
      onSubmit={onSubmit}
      validate={handleValidation}
      render={({ form, handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Flex width={1} flexDirection="column">
            <Flex flexDirection={['column', 'column', 'row']}>
              <Box width={1} mb={4} mr={3}>
                <Field
                  label={t`bank`}
                  name={bankAccountFormFields.BANK}
                  options={optionsBank}
                  placeholder={t`choose`}
                  render={SelectFilterField}
                />
              </Box>
              <Box width={1} mb={4}>
                <AccountTypeSelector bank={selectedBank} onChange={() => getChangeCallback(form)} />
              </Box>
            </Flex>
            <Flex flexDirection={['column', 'column', 'row']}>
              <Box mb={4} mr={[0, 0, 3]} width={[1, 1, `25%`, '18%', '35%']}>
                {/* @ts-ignore */}
                <InputLabelError
                  label={t`agency`}
                  name={bankAccountFormFields.AGENCY}
                  placeholder={selectedBank?.agencyPlaceholder || defaultPlaceholders.agency}
                />
              </Box>
              <AccountAndDigitInputs accountType={accountType} selectedBank={selectedBank} />
            </Flex>
          </Flex>
          <BankAccountFormControls isSubmiting={submitting} setOpenModal={handleOpenModal} />
        </form>
      )}
    />
  );
};
